import MuscleMassIcon from '../assets/icons/hugeicons_body-part-muscle.svg';
import BoneMassIcon from '../assets/icons/hugeicons_bone-01.svg';
import WeightIcon from '../assets/icons/hugeicons_weight-scale.svg';
import BodyFatIcon from '../assets/icons/hugeicons_percent-circle.svg';
import HeartPulseIcon from '../assets/icons/hugeicons_pulse-rectangle-01.svg';
import BloodPressureIcon from '../assets/icons/hugeicons_blood-pressure.svg';
import VitalsIcon from '../assets/icons/newDesign/vitals.svg';
import CameraIcon from '../assets/icons/newDesign/camera.svg';
import AssessmentIcon from '../assets/icons/newDesign/assessment.svg';
import EndSessionIcon from '../assets/icons/newDesign/endSession.svg';

// Import mapping icons
import bloodGlucoseIcon from '../assets/icons/newDesign/measurements/bloodGlucose.svg';
import bloodOxygenIcon from '../assets/icons/newDesign/measurements/bloodOxygen.svg';
import bloodPressureIcon from '../assets/icons/newDesign/measurements/bloodPressure.svg';
import bodyTemperatureIcon from '../assets/icons/newDesign/measurements/bloodTemperature.svg';
import bmiIcon from '../assets/icons/newDesign/measurements/bmo.svg';
import bodyFatRateIcon from '../assets/icons/newDesign/measurements/bodyFatRate.svg';
import bodyMuscleRateIcon from '../assets/icons/newDesign/measurements/bodyMuscleRate.svg';
import bodyWaterRateIcon from '../assets/icons/newDesign/measurements/bodyWaterRate.svg';
import boneMassIcon from '../assets/icons/newDesign/measurements/boneMass.svg';
import heartPulseIcon from '../assets/icons/newDesign/measurements/heartPulse.svg';
import weightIcon from '../assets/icons/newDesign/measurements/weight.svg';
import basalMetabolicRateIcon from '../assets/icons/newDesign/measurements/BasalMetabolicrate.svg';
import eyeIcon from '../assets/icons/newDesign/measurements/eye.svg';
import colorBlindnessIcon from '../assets/icons/newDesign/measurements/colorBlindess.svg';
import throatExamIcon from '../assets/icons/newDesign/measurements/throatExam.svg';
import skinExamIcon from '../assets/icons/newDesign/measurements/skinExam.svg';
import abdominalAuscultationExamIcon from '../assets/icons/newDesign/measurements/abdominalAuscultationExam.svg';
import earExamIcon from '../assets/icons/newDesign/measurements/earExam.svg';
import heartAuscultationExamIcon from '../assets/icons/newDesign/measurements/heartAuscultationExam.svg';
import lungsAuscultationExamIcon from '../assets/icons/newDesign/measurements/lungsAuscultationExam.svg';

import * as arabicTransliterate from 'arabic-transliterate';

export const STORAGE_KEYS = {
  APP_LANGUAGE: 'app-language',
  AI_LANGUAGE: 'ai-language',
};

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_BASE_URL?.replace(/=+$/, '') || '',
  ENDPOINTS: {
    SIGN_IN: '/mt-account/auth/sign-in',
    USER: '/mt-account/user',
    CURRENT_PATIENT: '/api/current_patient/',
    VITALS: '/vitals',
    CONSULTATION: '/consultation',
    QUESTIONNAIRE: `${process.env.REACT_APP_QUESTIONNAIRE_BASE_URL}/api/dynamic/balsamee-questionnaire`,
    POD_IP: (ip) => `/mt-vmr/pod/ip/${ip}`,
    PATIENT: (patientId) => `/mt-vmr/patient/${patientId}`,
    POD_CONSULTATION_EMAILS: '/mt-vmr/pod/consultation-emails',
    SUBMIT_QUESTIONNAIRE: `${process.env.REACT_APP_QUESTIONNAIRE_BASE_URL}/api/dynamic/balsamee-submit-questionnaire`,
    ACTIVE_QR: (podId) => `/mt-vmr/pod/${podId}/active-qr`,
    REGENERATE_QR: (podId) => `/mt-vmr/pod/${podId}/regenerate-qr`,
    START_SESSION_QR: '/mt-vmr/pod/start-session-qr',
    CHECK_VITALS: '/mt-vmr/pod/check-vitals',
    // start-guest-session is assumed to follow the existing /mt-vmr gateway
    // prefix used by the other pod endpoints above — the reference curl hit
    // the raw pod service directly on a local port (:8084) without it.
    // Adjust if incorrect.
    CREATE_GUEST: '/mt-account/guests',
    START_GUEST_SESSION: '/mt-vmr/pod/start-guest-session'
  }
};

// LiveAvatar API Configuration
export const LIVEAVATAR_CONFIG = {
  API_URL: 'https://api.liveavatar.com/v1',
  API_KEY: process.env.REACT_APP_LIVEAVATAR_API_KEY?.replace(/=+$/, ''),
  AVATAR_ID: process.env.REACT_APP_LIVEAVATAR_AVATAR_ID?.replace(/=+$/, ''),
  VOICE_ID: process.env.REACT_APP_LIVEAVATAR_VOICE_ID?.replace(/=+$/, ''),
  EN_CONTEXT_ID: process.env.REACT_APP_LIVEAVATAR_EN_CONTEXT_ID?.replace(/=+$/, ''),
  AR_CONTEXT_ID: process.env.REACT_APP_LIVEAVATAR_AR_CONTEXT_ID?.replace(/=+$/, '')
};

// Custom Avatar (Balsamee) API Configuration
export const CUSTOM_AVATAR_CONFIG = {
  BASE_URL: process.env.REACT_APP_CUSTOM_AVATAR_URL?.replace(/=+$/, '') || '',
};

// D-ID Agents SDK Configuration (https://docs.d-id.com/reference/agents-sdk-overview)
export const DID_AGENT_CONFIG = {
  CLIENT_KEY: process.env.REACT_APP_DID_CLIENT_KEY?.replace(/=+$/, ''),
  AGENT_ID_AR: process.env.REACT_APP_DID_AGENT_ID_AR?.replace(/=+$/, ''),
  AGENT_ID_EN: process.env.REACT_APP_DID_AGENT_ID_EN?.replace(/=+$/, '')
};

// Text API Message Enums
export const AI_COMMANDS = {
  REPEAT_INSTRUCTIONS: 'REPEAT_INSTRUCTIONS',
  SKIP: 'SKIP'
};

/** WebSocket event names used across the app. */
export const WS_EVENTS = {
  SESSION_STARTED: 'SESSION_STARTED',
  SESSION_ENDED: 'SESSION_ENDED',
  VITALS: 'Vitals',
  VITALS_UPDATE: 'vitals_update',
  // On multi-device (new flow) pods, "Vitals" is shared by all 3 devices — the
  // payload's "source" field (HIGO/LEKA/OMRON) tells them apart. Non-numeric
  // readings (images/audio, e.g. a skin exam) arrive on this separate event instead.
  VISUAL_MEDIA_READY: 'VisualMediaReady',
  ECG_IMAGE_READY: 'EcgImageReady',
  CONSULTATION_UPDATE: 'consultation_update',
  POD_UPDATE: 'pod_update',
  ERROR: 'error',
  CONNECTION: 'connection',
  MESSAGE: 'message',
  RECONNECT: 'reconnect',
  CHECKOUT_DONE: 'checkout_done'
};

/** Devices reporting vitals independently on "new flow" (multi-device) pods. */
export const VITALS_DEVICES = ['HIGO', 'LEKA', 'OMRON'];

/**
 * Default source priority when multiple devices report the same measurement
 * type on a multi-device pod: earlier device in the array wins.
 */
export const DEFAULT_VITALS_SOURCE_PRIORITY = ['LEKA', 'HIGO', 'OMRON'];

/**
 * Measurement types that ignore source priority entirely: whichever device
 * most recently sent an event containing that type wins, replacing the
 * previous reading regardless of which device reported it. Falls back to
 * DEFAULT_VITALS_EXCEPTION_SOURCE_PRIORITY only until a device has reported
 * the type at least once (e.g. right after a session reset).
 */
export const VITALS_PRIORITY_EXCEPTION_TYPES = ['HEART_RATE', 'SYSTOLIC', 'DIASTOLIC'];

/** Fallback source priority for VITALS_PRIORITY_EXCEPTION_TYPES before any device has reported them. */
export const DEFAULT_VITALS_EXCEPTION_SOURCE_PRIORITY = ['OMRON', 'LEKA', 'HIGO'];

/**
 * Devices whose Vitals-event readings are ignored entirely (never shown,
 * even as a fallback when no other device reports that measurement type).
 * Does NOT affect other event types from the same device (e.g. Higo visual
 * media is still kept). Add/remove entries here as sources change.
 */
export const DEFAULT_VITALS_EXCLUDED_SOURCES = ['HIGO'];

/**
 * Per-pod overrides for vitals source priority/exclusion, keyed by pod name
 * (podData.name). Any pod not listed here falls back to the
 * DEFAULT_VITALS_* settings above. Add an entry to give a specific pod
 * its own priority flow, e.g.:
 *   'Riyadh Mall Pod': {
 *     default: ['OMRON', 'LEKA', 'HIGO'],
 *     exceptions: ['HEART_RATE', 'SYSTOLIC', 'DIASTOLIC'],
 *     exceptionPriority: ['LEKA', 'OMRON', 'HIGO'],
 *     excludedSources: ['HIGO'],
 *   },
 */
export const POD_VITALS_SOURCE_PRIORITY = {};

/** Questionnaire/assessment types used for fetching and sequencing. */
export const QUESTIONNAIRE_TYPE = {
  MENTAL_HEALTH: 'clinical-mental-health',
  WELLBEING: 'wellbeing-lifestyle',
  ORGANIZATIONAL_PSYCHOLOGY:'work-organizational-psychology',
  MEDICAL_RISK: 'health-screening-medical-risk'

};

/**
 * Dashboard action buttons configuration.
 * Uses translation keys (labelKey) – resolve with t(labelKey) in the component.
 */
export const DASHBOARD_BUTTON_CONFIG = [
  {
    id: 'vitals',
    icon: VitalsIcon,
    labelKey: 'dashboard.checkVitals',
    action: 'navigate',
  route: '/vitals',
  },
  {
    id: 'consultation',
    icon: CameraIcon,
    labelKey: 'dashboard.joinConsultation',
    action: 'navigate',
    route: '/consultation',
  },
  {
    id: 'assessment',
    icon: AssessmentIcon,
    labelKey: 'dashboard.healthAssessment',
    action: 'navigate',
    route: '/assessment',
  },
  {
    id: 'end',
    icon: EndSessionIcon,
    labelKey: 'dashboard.endSession',
    action: 'endSession',
  },
];

// App Configuration
export const APP_CONFIG = {
  APP_NAME: 'Medical Pod',
  VERSION: '1.0.0',
  ROUTES: {
    DASHBOARD: '/',
    WELCOME: '/welcome',
    VITALS: '/vitals',
    CONSULTATION: '/consultation/2',
    ASSESSMENT: '/assessment',
    END_SESSION: '/end'
  }
};

// Mock Data
export const MOCK_DATA = {
  USER: {
    name: 'Guest',
    age: 30,
    gender: 'Male',
    patientId: '68'
  },
  VITALS: {
    standing: [
      { name: 'Muscle Mass', value: '74.5', unit: 'kg', icon: MuscleMassIcon },
      { name: 'Weight', value: '74.5', unit: 'kg', icon: WeightIcon },
      { name: 'Bone Mass', value: '74.5', unit: 'kg', icon: BoneMassIcon },
      { name: 'Heart pulse', value: '74.5', unit: 'bpm', icon: HeartPulseIcon },
      { name: 'Fat percentage', value: '74.5', unit: '%', icon: BodyFatIcon },
      { name: 'Blood pressure', value: '118 / 76', unit: 'mmHg', icon: BloodPressureIcon }
    ],
    sitting: [
      { name: 'BMI', value: '74.5', unit: '%', icon: '📊' },
      { name: 'Muscle Mass', value: '74.5', unit: '%', icon: '💪' },
      { name: 'Metabolic Age', value: '23', unit: 'years', icon: '⏰' },
      { name: 'Bone Mass', value: '74.5', unit: '%', icon: '🦴' },
      { name: 'Nerve Health', value: '74.5', unit: '%', icon: '🧠' },
      { name: 'Weight', value: '74.5', unit: 'kg', icon: '⚖️' },
      { name: 'Fat Ratio', value: '74.5', unit: '%', icon: '📈' },
      { name: 'Hydration', value: '74.5', unit: '%', icon: '💧' }
    ]
  },
  DOCTOR: {
    name: 'Dr. Mohammed Khaled',
    title: 'GP',
    avatar: '👨‍⚕️'
  }
};



export const VITAL_ICONS = {
  "GLUCOSE": bloodGlucoseIcon,
  "SPO2": bloodOxygenIcon,
  "SYSTOLIC": bloodPressureIcon,
  "DIASTOLIC": bloodPressureIcon,
  "TEMPERATURE": bodyTemperatureIcon,
  "BMI": bmiIcon,
  "BODY_FAT_CONTENT": bodyFatRateIcon,
  "BODY_FAT_RATE": bodyFatRateIcon,
  "BODY_MUSCLE_CONTENT": bodyMuscleRateIcon,
  "BODY_MUSCLE_RATE": bodyMuscleRateIcon,
  "BODY_WATER_CONTENT": bodyWaterRateIcon,
  "BODY_WATER_RATE": bodyWaterRateIcon,
  "BONE_MASS": boneMassIcon,
  "HEART_RATE": heartPulseIcon,
  "ECG": heartPulseIcon,
  "WEIGHT": weightIcon,
  "HEIGHT": null,
  "BASAL_METABOLIC_RATE": basalMetabolicRateIcon,
  "BASAL_METABOLISM": basalMetabolicRateIcon,
  "VISUAL_ACUITY_LEFT": eyeIcon,
  "VISUAL_ACUITY_RIGHT": eyeIcon,
  "COLOR_BLINDNESS": colorBlindnessIcon,
  "URIC_ACID": null,
  "HEART_AUSCULTATION_EXAM": heartAuscultationExamIcon,
  "LUNGS_AUSCULTATION_EXAM": lungsAuscultationExamIcon,
  "COUGH_EXAM": null,
  "ABDOMINAL_AUSCULTATION_EXAM": abdominalAuscultationExamIcon,
  "SKIN_EXAM": skinExamIcon,
  "THROAT_EXAM": throatExamIcon,
  "RIGHT_EAR_EXAM": earExamIcon,
  "LEFT_EAR_EXAM": earExamIcon
};

/** earExam.svg is drawn for the right ear by default — mirror it for these types. */
export const VITAL_ICON_MIRRORED_TYPES = ['LEFT_EAR_EXAM'];

/**
 * Non-numeric "visual media" exams (audio/image readings, no numeric value).
 * Cards for these render name-only, like ECG, and open a modal with the
 * captured files on click instead of showing a value.
 */
export const VISUAL_MEDIA_EXAM_TYPES = [
  'HEART_AUSCULTATION_EXAM',
  'LUNGS_AUSCULTATION_EXAM',
  'COUGH_EXAM',
  'ABDOMINAL_AUSCULTATION_EXAM',
  'SKIN_EXAM',
  'THROAT_EXAM',
  'RIGHT_EAR_EXAM',
  'LEFT_EAR_EXAM',
];

// Global Toast Settings
export const TOAST_CONFIG = {
  position: 'top-right',
  containerStyle: {
    top: 100,
  },
  toastOptions: {
    style: {
      fontSize: '2.5vw',
      fontWeight: 'var(--font-semibold, 600)',
      textAlign: 'center',
      maxWidth: 'fit-content',
      minWidth: '400px',
      zIndex: 9999999,
      borderRadius: '24px',
      padding: '24px 32px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
      fontFamily: 'var(--font-family, inherit)',
      background: '#ffffff',
      color: 'var(--text-dark, #333333)',
    },
    error: {
      duration: 5000,
      style: {
        background: '#FEF2F2',
        border: '2px solid #FECACA',
        color: '#991B1B',
      },
      iconTheme: {
        primary: '#EF4444',
        secondary: '#FEF2F2',
      },
    },
    success: {
      style: {
        background: '#F0FDF4',
        border: '2px solid #BBF7D0',
        color: '#166534',
      },
      iconTheme: {
        primary: '#22C55E',
        secondary: '#F0FDF4',
      },
    },
  },
};

export const AI_NAVIGATION_MESSAGES = {
  'dashboard': {
    "en": "Just navigated to dashboard",
    "ar": "تم الانتقال الى الرئيسية"
  },
  'vitals': {
    "en": "Just navigated to vitals",
    "ar": "فلنبدأ القياسات الحيوية"
  },
  'assessment': {
    "en": "Just navigated to assessment",
    "ar": "فلنبدأ التقييمات"
  },
}


export const getAiGreetingInstructionsMessage = (language, userName) => {
  // if (language === "ar") {
  //   userName = arabicTransliterate(userName, "latin2arabic", "Arabic")
  // }
  switch (language) {
    case "ar":
      return `مرحبًا بك في بَلسمِي يا ${userName}.
أنا ذكي، مساعدك الصحي الذكي ، وسأرشدك خلال جلستك داخل البودالطبية، يمكنك التحدث معي فى أي وقت، فقط قل يا ذكي وسأسمعك، عندما تجد الزِر يضيئ بالأخضر فهذا يعني أنّي قادرٌ على سماعك.
كيف يمكنني مساعدتك اليوم؟`
    case "en":
      return `Hello ${userName}, and welcome to Balsamee.
          I am Zaki, your AI Health Assistant, and I will guide you through your medical pod session, You can talk to me anytime by saying "Hey Zaki". When the button turns green, this means that I can hear you. How can I assist you?`
    default:
      return `Hello ${userName}, and welcome to Balsamee.
          I am Zaki, your AI Health Assistant, and I will guide you through your medical pod session, You can talk to me anytime by saying "Hey Zaki". When the button turns green, this means that I can hear you. How can I assist you?`

  }
}

/**
 * Wake word keywords for background voice activation.
 * Each entry is matched as a case-insensitive substring of the recognised transcript.
 */
export const WAKE_WORD_KEYWORDS_EN = ["Zaki", "zaki", "Thaki", "thaki", "theki", "Theki", "Zaky", "zaky", "Thaky", "thaky", "Theky", "theky", "Hey", "hey",];
export const WAKE_WORD_KEYWORDS_AR = ["ذكي", "ذكي","زكي"];

export const ASSESSMENT_CUSTOM_AVATAR_IDS = {
  [QUESTIONNAIRE_TYPE.WELLBEING]: "wellbeing-lifestyle",
  [QUESTIONNAIRE_TYPE.MENTAL_HEALTH]: "clinical-mental-health",
  [QUESTIONNAIRE_TYPE.MEDICAL_RISK]: "health-screening-medical-risk",
  [QUESTIONNAIRE_TYPE.ORGANIZATIONAL_PSYCHOLOGY]:"work-organizational-psychology"
}
